import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAuthAPI } from '../../services/api/adminAuthAPI';
import { initializeIpErrorHandling } from '../../utils/adminAuth';
import { 
  getAdminEndpoint, 
  setAdminEndpoint, 
  getAllAdminEndpoints,
  PRESET_ENDPOINTS,
  resetToDefaultEndpoint 
} from '../../services/endpoints/adminEndpointManager';
import './Login.css';

const Login = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  
  // State for authentication
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Setup mode state
  const [setupMode, setSetupMode] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  
  // Login form state
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Setup form state
  const [setupData, setSetupData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });

  // Environment configuration state
  const [envEndpoint, setEnvEndpoint] = useState('');
  const [showEnvConfig, setShowEnvConfig] = useState(false);
  const [envError, setEnvError] = useState('');
  const [showEndpoints, setShowEndpoints] = useState(false);
  const [endpointInfo, setEndpointInfo] = useState(null);
  const [currentPresetName, setCurrentPresetName] = useState('');

  // Initialize environment endpoint from config
  useEffect(() => {
    const endpoint = getAdminEndpoint();
    setEnvEndpoint(endpoint);
    updateEndpointInfo(endpoint);
    updateCurrentPresetName(endpoint);
  }, []);

  // Update endpoint info for display
  const updateEndpointInfo = (url) => {
    const info = getAllAdminEndpoints();
    info.baseUrl = url;
    setEndpointInfo(info);
  };

  // Update current preset name based on URL
  const updateCurrentPresetName = (url) => {
    const preset = PRESET_ENDPOINTS.find(p => p.url === url);
    setCurrentPresetName(preset ? preset.name : 'Custom');
  };

  // Check if setup is required
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        setCheckingSetup(true);
        const status = await adminAuthAPI.checkSetupStatus();
        setSetupMode(status.setupRequired || false);
        console.log('Setup status:', status);
      } catch (error) {
        console.log('Setup status check failed:', error.message);
        setSetupMode(false);
      } finally {
        setCheckingSetup(false);
      }
    };
    
    checkSetupStatus();
  }, []);

  // Initialize error handling
  useEffect(() => {
    initializeIpErrorHandling();
    console.log('Login component mounted');

    const handleIpError = (event) => {
      setError(`IP Error: ${event.detail.errorMessage}`);
      setLoading(false);
    };

    window.addEventListener('ip-validation-error', handleIpError);

    return () => {
      window.removeEventListener('ip-validation-error', handleIpError);
    };
  }, []);

  // Handle password-based login
  const handlePasswordLogin = async () => {
    if (!usernameOrEmail || !password) {
      setError('Please enter username/email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Processing password-based login...');
      
      const result = await adminAuthAPI.login({
        usernameOrEmail,
        password,
        sessionDuration: 24,
        deviceName: `${navigator.userAgent}`
      });
      
      console.log('Login successful:', result);
      setIsAuthenticated(true);
      setLoading(false);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  // Handle initial setup (first super admin)
  const handleSetup = async () => {
    if (!setupData.username || !setupData.email || !setupData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (setupData.password !== setupData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Password requirements check
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(setupData.password)) {
      setError('Password must be at least 8 characters with uppercase, lowercase, number, and special character');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Creating initial admin setup...');
      
      const result = await adminAuthAPI.setup({
        username: setupData.username,
        email: setupData.email,
        password: setupData.password,
        confirmPassword: setupData.confirmPassword,
        firstName: setupData.firstName,
        lastName: setupData.lastName
      });
      
      console.log('Setup successful:', result);
      setIsAuthenticated(true);
      setLoading(false);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Setup error:', error);
      setError(error.response?.data?.message || 'Setup failed. Please try again.');
      setLoading(false);
    }
  };

  // Save environment configuration
  const handleSaveEnvConfig = () => {
    if (!envEndpoint) {
      setEnvError('Please enter a valid endpoint URL');
      return;
    }
    
    try {
      setAdminEndpoint(envEndpoint);
      updateEndpointInfo(envEndpoint);
      updateCurrentPresetName(envEndpoint);
      setEnvError('');
      setShowEnvConfig(false);
      // Reload page to apply new configuration
      window.location.reload();
    } catch (error) {
      setEnvError('Failed to save configuration: ' + error.message);
    }
  };

  // Use preset endpoint
  const handleUsePreset = (url) => {
    setEnvEndpoint(url);
    setAdminEndpoint(url);

    // Clear all localStorage keys that might cache old endpoint data
    localStorage.removeItem('custom-env-config');
    localStorage.removeItem('config-service-endpoint');
    localStorage.removeItem('selected-endpoint');
    localStorage.removeItem('available-endpoints');
    localStorage.removeItem('admin-token');
    localStorage.removeItem('admin-session');
    localStorage.removeItem('admin-user');

    updateEndpointInfo(url);
    updateCurrentPresetName(url);
    setShowEnvConfig(false);
    window.location.reload();
  };

  // Reset to default
  const handleResetEndpoint = () => {
    const defaultUrl = resetToDefaultEndpoint();
    setEnvEndpoint(defaultUrl);
    updateEndpointInfo(defaultUrl);
    updateCurrentPresetName(defaultUrl);
    setShowEnvConfig(false);
    window.location.reload();
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="login-left-content">
          <h1>Admin Dashboard</h1>
          <p>
            Secure administration platform with password-based authentication.
            Login to access the dashboard.
          </p>
          
          <div className="login-info">
            <h3>Security Features</h3>
            <ul>
              <li>🔐 Password-based authentication</li>
              <li>🛡️ Role-based access control</li>
              <li>⏰ Session management with JWT</li>
              <li>🔒 IP-based session binding</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="login-right">
        <div className="login-card">
          <div className="form-logo">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="80" height="80" rx="12" fill="#2D3436"/>
              <path d="M16 55L28 40L38 48L52 28L64 38" stroke="#00B894" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="52" cy="28" r="4" fill="#00B894"/>
              <path d="M16 65H64" stroke="#636E72" strokeWidth="2" strokeLinecap="round"/>
              <path d="M20 20V14M30 20V10M40 20V14M50 20V10M60 20V14" stroke="#636E72" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          
          <h2>{setupMode ? 'Initial Setup' : 'Admin Login'}</h2>
          
          {checkingSetup && (
            <div className="loading-setup">
              <span className="spinner"></span>
              <p>Checking system status...</p>
            </div>
          )}
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {/* Backend Configuration */}
          <div className="env-config-section">
            {/* Current Configuration Display */}
            <div className="current-config-display">
              <div className="config-info">
                <span className="config-label">Backend Config:</span>
                <span className={`config-badge config-${currentPresetName.toLowerCase()}`}>
                  {currentPresetName}
                </span>
                <span className="config-url" title={envEndpoint}>{envEndpoint}</span>
              </div>
              <button
                type="button"
                className="config-toggle-btn"
                onClick={() => setShowEnvConfig(!showEnvConfig)}
              >
                {showEnvConfig ? '▲' : '▼'}
              </button>
            </div>
            
            {showEnvConfig && (
              <div className="env-config-form">
                <div className="form-group">
                  <label>Backend API Endpoint</label>
                  <input
                    type="text"
                    value={envEndpoint}
                    onChange={(e) => setEnvEndpoint(e.target.value)}
                    placeholder="http://localhost:5000"
                  />
                </div>
                
                {envError && (
                  <div className="env-error">{envError}</div>
                )}
                
                <div className="env-actions">
                  <button
                    type="button"
                    onClick={handleSaveEnvConfig}
                    className="env-save-btn"
                  >
                    Save & Reload
                  </button>
                  <button
                    type="button"
                    onClick={handleResetEndpoint}
                    className="env-reset-btn"
                  >
                    Reset to Default
                  </button>
                </div>
                
                <div className="preset-endpoints">
                  <label>Quick Select:</label>
                  <div className="preset-buttons">
                    {PRESET_ENDPOINTS.map((preset) => (
                      <button
                        key={preset.name}
                        type="button"
                        onClick={() => handleUsePreset(preset.url)}
                        className={`preset-btn ${envEndpoint === preset.url ? 'preset-active' : ''}`}
                      >
                        {preset.name}
                        {envEndpoint === preset.url && ' ✓'}
                      </button>
                    ))}
                  </div>
                </div>
                
                <button
                  type="button"
                  className="endpoints-toggle-btn"
                  onClick={() => setShowEndpoints(!showEndpoints)}
                >
                  {showEndpoints ? 'Hide' : 'Show'} Available Endpoints
                </button>
                
                {showEndpoints && endpointInfo && (
                  <div className="endpoints-list">
                    <div className="endpoint-base">
                      <strong>Base URL:</strong> {endpointInfo.baseUrl}
                    </div>
                    <div className="endpoint-group">
                      <h4>Auth Endpoints:</h4>
                      <ul>
                        {endpointInfo.auth.endpoints.map((ep) => (
                          <li key={ep.name}>
                            <code>{ep.path}</code>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="endpoint-group">
                      <h4>Management Endpoints:</h4>
                      <ul>
                        {endpointInfo.management.endpoints.map((ep) => (
                          <li key={ep.name}>
                            <code>{ep.path}</code>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Setup Form */}
          {setupMode ? (
            <div className="setup-form">
              <div className="setup-header">
                <span className="setup-icon">🚀</span>
                <p className="setup-notice">
                  Welcome! Create your <strong>super admin</strong> account to initialize the system.
                </p>
              </div>
              
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={setupData.username}
                  onChange={(e) => setSetupData({...setupData, username: e.target.value})}
                  placeholder="admin"
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={setupData.email}
                  onChange={(e) => setSetupData({...setupData, email: e.target.value})}
                  placeholder="admin@example.com"
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={setupData.password}
                  onChange={(e) => setSetupData({...setupData, password: e.target.value})}
                  placeholder="Min 8 chars: uppercase, lowercase, number, special"
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  value={setupData.confirmPassword}
                  onChange={(e) => setSetupData({...setupData, confirmPassword: e.target.value})}
                  placeholder="Confirm your password"
                  disabled={loading}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={setupData.firstName}
                    onChange={(e) => setSetupData({...setupData, firstName: e.target.value})}
                    placeholder="Super"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={setupData.lastName}
                    onChange={(e) => setSetupData({...setupData, lastName: e.target.value})}
                    placeholder="Admin"
                    disabled={loading}
                  />
                </div>
              </div>
              
              <button
                type="button"
                onClick={handleSetup}
                className="login-btn"
                disabled={loading}
              >
                {loading ? 'Creating Admin...' : 'Create Super Admin'}
              </button>
            </div>
          ) : (
            /* Login Form */
            <div className="login-form">
              <div className="form-group">
                <label>Username or Email</label>
                <input
                  type="text"
                  value={usernameOrEmail}
                  onChange={(e) => setUsernameOrEmail(e.target.value)}
                  placeholder="admin or admin@example.com"
                  disabled={loading}
                  autoFocus
                />
              </div>
              
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={loading}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordLogin()}
                />
              </div>
              
              <button
                type="button"
                onClick={handlePasswordLogin}
                className="login-btn"
                disabled={loading}
              >
                {loading ? 'Authenticating...' : 'Login'}
              </button>
              
              {/* Manual Setup Button */}
              <button
                type="button"
                onClick={() => setSetupMode(true)}
                className="setup-link-btn"
              >
                🚀 First time? Create super admin account
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
